import { Grid3x3, Pencil, ShieldCheck, Trash2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Role } from '../types'
import { useAccessLookups } from '../useLookups'
import { ScopeBadge, StatusBadge } from './AccessBits'
import { RoleHistory } from './RoleHistory'
import { RolePermissionSummary } from './RolePermissionSummary'
import { RoleUsersTable } from './RoleUsersTable'
import type { RoleCommands } from './useRoleCommands'

type Props = {
  role: Role
  canWrite: boolean
  commands: RoleCommands
  onEdit: () => void
  onAssign: () => void
}

export function RoleSummaryPanel({ role, canWrite, commands, onEdit, onAssign }: Props) {
  const { t } = useTranslation()
  const { targetNames } = useAccessLookups()
  const targets = targetNames(role)

  return (
    <section className="rounded-2xl border bg-card">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b p-4">
        <div className="flex min-w-0 gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-orange/12 text-brand-orange sm:size-11 sm:rounded-xl">
            <ShieldCheck className="size-4 sm:size-5" />
          </span>
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">{role.name}</h2>
              <StatusBadge status={role.status} />
              <ScopeBadge scope={role.scope} />
            </div>
            <p className="text-sm text-muted-foreground">{role.description}</p>
            {targets.length ? <p className="text-xs text-muted-foreground">{targets.join(' · ')}</p> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link to={`/access/roles/${role.id}`}>
              <Grid3x3 />
              {t('access.roles.actions.openDetail')}
            </Link>
          </Button>
          {canWrite ? (
            <>
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Pencil />
                {t('access.roles.actions.edit')}
              </Button>
              <Button size="sm" variant="outline" onClick={onAssign}>
                <UserPlus />
                {t('access.roles.actions.assignUsers')}
              </Button>
              {!role.isSystem ? (
                <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => commands.requestDelete(role)}>
                  <Trash2 />
                  {t('access.roles.actions.delete')}
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      <Tabs defaultValue="permissions" className="p-4 pt-2">
        <TabsList>
          <TabsTrigger value="permissions">{t('access.summary.tabPermissions')}</TabsTrigger>
          <TabsTrigger value="users">{t('access.summary.tabUsers', { count: role.assignments.length })}</TabsTrigger>
          <TabsTrigger value="history">{t('access.summary.tabHistory')}</TabsTrigger>
        </TabsList>
        <TabsContent value="permissions" className="mt-4">
          <RolePermissionSummary permissions={role.permissions} permissionItems={role.permissionItems} />
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <RoleUsersTable role={role} canWrite={canWrite} onAssign={onAssign} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <RoleHistory roleId={role.id} />
        </TabsContent>
      </Tabs>
    </section>
  )
}
